import { useEffect, useRef, useState } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { Skeleton } from '@/components/ui/skeleton'
import { logger } from '@app/utils/logger'

// pdf.js creates its own worker from this URL on first use. Vite's `?url` suffix gives a
// hashed asset URL for the worker bundled from the same package version as the API (avoids
// worker/API version mismatches from pulling the worker off a CDN separately).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

type PdfPreviewProps = {
  // Presigned S3 URL, not a documentId — fed straight to pdf.js, which issues its own
  // uncredentialed Range GETs directly against S3.
  url: string
  onError: () => void
}

export const PdfPreview = ({ url, onError }: PdfPreviewProps) => {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setDoc(null)

    // Both settings are needed to keep fetching bounded to Range GETs for pages actually
    // viewed: disableStream stops pdf.js's first (non-Range) request from staying open as an
    // ongoing stream, and disableAutoFetch stops it proactively streaming the rest of the file
    // in the background.
    const loadingTask = pdfjsLib.getDocument({ url, disableAutoFetch: true, disableStream: true })
    loadingTask.promise.then(
      (pdf) => {
        if (cancelled) return
        setDoc(pdf)
        setIsLoading(false)
      },
      (error) => {
        if (cancelled) return
        logger.error('Error loading PDF document:', error)
        onError()
      },
    )

    return () => {
      cancelled = true
      loadingTask.destroy()
    }
  }, [url, onError])

  if (isLoading) {
    return <Skeleton className="h-full w-full" />
  }

  if (!doc) return null

  return (
    <div
      ref={scrollContainerRef}
      className="flex h-full flex-col items-center gap-4 overflow-y-auto rounded-md border bg-slate-50 p-4"
    >
      {Array.from({ length: doc.numPages }, (_, i) => i + 1).map((pageNumber) => (
        <PdfPage
          key={pageNumber}
          doc={doc}
          pageNumber={pageNumber}
          scrollContainerRef={scrollContainerRef}
          onError={onError}
        />
      ))}
    </div>
  )
}

// A typical page's height at a readable width — used as the unrendered placeholder size so the
// scroll container has a stable, roughly-accurate height (and thus a usable scrollbar/intersection
// geometry) before any page has actually been fetched and rendered.
const PLACEHOLDER_HEIGHT_PX = 800

type PdfPageProps = {
  doc: PDFDocumentProxy
  pageNumber: number
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  onError: () => void
}

const PdfPage = ({ doc, pageNumber, scrollContainerRef, onError }: PdfPageProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isRendered, setIsRendered] = useState(false)
  // Guards the render effect against re-running itself — see the comment on that effect's
  // dependency array below for why this can't just be the `isRendered` state.
  const hasStartedRenderRef = useRef(false)

  // Lazy-render trigger: only fetch/render a page once it's scrolled near view, rather than
  // the whole document upfront. Reads scrollContainerRef.current inside the effect rather than
  // taking the resolved element as a prop — PdfPreview's own ref attaches in the same render
  // pass that creates every PdfPage, so a prop would freeze on a stale null; effects run after
  // the tree commits, so the ref is populated by the time this runs.
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || !wrapperRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true)
      },
      { root: scrollContainer, rootMargin: '200px 0px' },
    )
    observer.observe(wrapperRef.current)
    return () => observer.disconnect()
  }, [scrollContainerRef])

  useEffect(() => {
    if (!isVisible || hasStartedRenderRef.current) return
    hasStartedRenderRef.current = true
    let cancelled = false
    let page: PDFPageProxy | null = null
    let renderTask: ReturnType<PDFPageProxy['render']> | null = null

    doc
      .getPage(pageNumber)
      .then((loadedPage) => {
        if (cancelled) return
        page = loadedPage
        const canvas = canvasRef.current
        const container = wrapperRef.current
        if (!canvas || !container) throw new Error('PDF page canvas not mounted')

        const unscaledViewport = page.getViewport({ scale: 1 })
        const scale = container.clientWidth / unscaledViewport.width
        const viewport = page.getViewport({ scale })
        canvas.width = viewport.width
        canvas.height = viewport.height

        // Pass canvas alone, not canvasContext too — pdfjs-dist treats canvasContext as a
        // backwards-compat path requiring canvas to be null; passing both silently renders
        // nothing rather than throwing.
        renderTask = page.render({ canvas, viewport })
        return renderTask.promise
      })
      .then(() => {
        if (!cancelled) setIsRendered(true)
      })
      .catch((error) => {
        // A cancelled render task rejects too — that's expected on unmount/re-trigger, not a
        // real failure, so don't surface it as one.
        if (cancelled || error?.name === 'RenderingCancelledException') return
        logger.error(`Error rendering PDF page ${pageNumber}:`, error)
        onError()
      })

    return () => {
      cancelled = true
      renderTask?.cancel()
      page?.cleanup()
    }
    // isRendered isn't a dependency here (hasStartedRenderRef guards re-entry instead) — since
    // this effect sets isRendered, including it would re-trigger cleanup right after a
    // successful render, blanking the canvas straight after it drew.
  }, [doc, pageNumber, isVisible, onError])

  return (
    <div
      ref={wrapperRef}
      className="w-full max-w-2xl bg-white shadow-sm"
      style={!isRendered ? { minHeight: PLACEHOLDER_HEIGHT_PX } : undefined}
    >
      <canvas ref={canvasRef} className="w-full" />
    </div>
  )
}
